import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: ReactNode;
  ayuda?: string;
  /** Va a la `<label>` envolvente (la fila completa), no al `<input>`. */
  className?: string;
}

/**
 * Casilla nativa con `accent-primary`, sin Radix: cero dependencias, se dibuja
 * como la del sistema en el celular y la `<label>` que la envuelve le da el
 * objetivo tactil de 44px que la casilla de 16px no tiene. Como la label
 * envuelve al input, no hace falta `htmlFor`: tocar el texto tilda.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, ayuda, className, ...props }, ref) => (
    <label
      className={cn(
        "flex min-h-11 cursor-pointer items-start gap-2 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50 sm:min-h-0",
        className,
      )}
    >
      <input
        ref={ref}
        type="checkbox"
        className="mt-0.5 size-4 shrink-0 cursor-pointer rounded-sm border-input accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed"
        {...props}
      />
      <span className="min-w-0">
        <span className="block text-sm font-medium leading-tight">{label}</span>
        {ayuda ? <span className="block text-xs text-muted-foreground">{ayuda}</span> : null}
      </span>
    </label>
  ),
);
Checkbox.displayName = "Checkbox";
